import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import {
    Bar, BarChart, CartesianGrid,
    ErrorBar,
    ResponsiveContainer,
    Tooltip,
    XAxis, YAxis,
} from 'recharts';
import { FadedPaper } from '../../theme/StyledFields';
import KeyTakeaway from './KeyTakeaway';
import VizToggle, { DEFAULT_REPORT_VIZ, VizType } from './VizToggle';
import { ReportSection, VarianceRow } from './types';

interface Props {
    data: ReportSection<VarianceRow>;
}

const VarianceSection: React.FC<Props> = ({ data }) => {
    const [viz, setViz] = useState<VizType>(DEFAULT_REPORT_VIZ);
    const theme = useTheme();

    const rangeData = data.rows.map((r) => ({
        domain: r.domain_name,
        min: r.min_score,
        max: r.max_score,
        avg: r.avg_score,
        range: r.score_range,
        lowerErr: r.avg_score - r.min_score,
        upperErr: r.max_score - r.avg_score,
        stddev: r.stddev,
    }));

    const barData = data.rows.map((r) => ({
        domain: r.domain_name,
        stddev: r.stddev,
    }));

    return (
        <FadedPaper sx={{ p: 3, mb: 3 }} data-testid="section-variance">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5">Team Alignment Across Skill Areas</Typography>
                <VizToggle value={viz} options={['range', 'bar', 'table']} onChange={setViz} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                High standard deviation means the team is split. Low means consensus.
            </Typography>

            {viz === 'range' && (
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={rangeData} layout="vertical" margin={{ left: 120 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 5]} />
                        <YAxis type="category" dataKey="domain" tick={{ fontSize: 12 }} width={110} />
                        <Tooltip formatter={(val: number) => val.toFixed(2)} />
                        <Bar dataKey="avg" name="Average" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]}>
                            <ErrorBar dataKey="lowerErr" direction="x" width={4} stroke={theme.palette.error.main} />
                            <ErrorBar dataKey="upperErr" direction="x" width={4} stroke={theme.palette.error.main} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}

            {viz === 'bar' && (
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={barData} layout="vertical" margin={{ left: 120 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="domain" tick={{ fontSize: 12 }} width={110} />
                        <Tooltip />
                        <Bar dataKey="stddev" name="Std Dev" fill={theme.palette.secondary.main} radius={[0, 4, 4, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            )}

            {viz === 'table' && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Rank</TableCell>
                                <TableCell>Domain</TableCell>
                                <TableCell align="right">Avg</TableCell>
                                <TableCell align="right">Min</TableCell>
                                <TableCell align="right">Max</TableCell>
                                <TableCell align="right">Range</TableCell>
                                <TableCell align="right">StdDev</TableCell>
                                <TableCell align="right">n</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.rows.map((r) => (
                                <TableRow key={r.domain_name}>
                                    <TableCell>{r.most_polarized_rank}</TableCell>
                                    <TableCell>{r.domain_name}</TableCell>
                                    <TableCell align="right">{r.avg_score}</TableCell>
                                    <TableCell align="right">{r.min_score}</TableCell>
                                    <TableCell align="right">{r.max_score}</TableCell>
                                    <TableCell align="right">{r.score_range}</TableCell>
                                    <TableCell align="right">{r.stddev}</TableCell>
                                    <TableCell align="right">{r.n}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <KeyTakeaway text={data.key_takeaway} />
        </FadedPaper>
    );
};

export default VarianceSection;
